/**
 * "Turn on alerts": our own prompt before the browser's permission dialog (which can only open after
 * a tap, and which a client who says no can't easily undo). Shown when it helps:
 *   - the first time the Home Screen app opens signed in,
 *   - after the client places an order, sets a price alert or gets a deposit credited (nudgePush).
 * "Not now" hides it for a week. On an iPhone in Safari, where push needs the Home Screen app, the
 * same moments offer "Add to Home Screen" instead.
 *
 * usePushSync keeps this device subscribed while signed in and the app icon badge in step with the
 * unread count.
 */
import { useEffect, useState } from 'react';
import { BRAND } from '@platform/shared';
import { isStandalone } from '../lib/install';
import { enablePush, optedOut, promptSnoozed, pushStore, registerServiceWorker, snoozePrompt, syncPush, usePushState } from '../lib/push';
import { useUnreadCount } from '../stores/notifications';
import { useMe } from '../stores/session';
import { useAddToHomeScreen } from './mobile/InstallSheet';
import { Icon } from './ui';

const FIRST_OPEN_KEY = 'pushPromptAppOpen';

export function usePushSync() {
  const me = useMe();
  const userId = me?.id ?? null;
  // A staff session opened from the CRM never subscribes the staff member's browser.
  const staff = !!me?.signedInBy;
  useEffect(() => {
    void registerServiceWorker();
  }, []);
  useEffect(() => {
    if (!userId || staff) return;
    void syncPush();
    // Coming back to the app (e.g. after allowing notifications in Settings) re-checks the permission.
    const onShow = () => document.visibilityState === 'visible' && void syncPush();
    document.addEventListener('visibilitychange', onShow);
    return () => document.removeEventListener('visibilitychange', onShow);
  }, [userId, staff]);

  const unread = useUnreadCount();
  useEffect(() => {
    const n = navigator as Navigator & { setAppBadge?: (n: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
    if (!userId) n.clearAppBadge?.().catch(() => {});
    else if (unread > 0) n.setAppBadge?.(unread).catch(() => {});
    else n.clearAppBadge?.().catch(() => {});
  }, [unread, userId]);
}

const COPY = {
  order: 'Get notified the moment your take profit or stop loss is hit, and when orders fill.',
  price_alert: 'Turn on notifications so your price alert reaches you even when the app is closed.',
  deposit: 'Get notified about deposits, withdrawals and your trades, right on your lock screen.',
  open: 'Trade results, margin warnings, price alerts and account updates, right on your lock screen.',
} as const;

export function PushPrompt() {
  const me = useMe();
  const s = usePushState();
  const [firstOpen, setFirstOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const install = useAddToHomeScreen();

  // First signed-in open of the Home Screen app: offer alerts after a moment, once.
  useEffect(() => {
    if (!me || !isStandalone()) return;
    let seen = false;
    try {
      seen = localStorage.getItem(FIRST_OPEN_KEY) === '1';
    } catch {
      /* ignore */
    }
    if (seen) return;
    const t = setTimeout(() => {
      setFirstOpen(true);
      try {
        localStorage.setItem(FIRST_OPEN_KEY, '1');
      } catch {
        /* ignore */
      }
    }, 2500);
    return () => clearTimeout(t);
  }, [me]);

  if (!me || me.signedInBy) return install.sheet;
  const reason = s.nudge ?? (firstOpen ? 'open' : null);
  if (!reason || promptSnoozed()) return install.sheet;

  const close = () => {
    snoozePrompt();
    setFirstOpen(false);
  };

  // iPhone / iPad in Safari: push needs the Home Screen app.
  if (s.support === 'ios_needs_install') {
    if (reason === 'open') return install.sheet;
    return (
      <>
        <Card
          title="Get alerts on your iPhone"
          text={`Add ${BRAND.shortName} to your Home Screen, open it from there and turn on notifications. iPhone only sends web app notifications to Home Screen apps.`}
          primary="Show me how"
          onPrimary={() => {
            void install.start();
            close();
          }}
          onClose={close}
        />
        {install.sheet}
      </>
    );
  }
  if (s.support !== 'supported' || s.permission !== 'default' || s.serverEnabled === false || optedOut()) return install.sheet;

  return (
    <Card
      title="Turn on alerts"
      text={COPY[reason]}
      primary="Turn on"
      busy={busy}
      onPrimary={async () => {
        setBusy(true);
        try {
          await enablePush();
        } finally {
          setBusy(false);
          setFirstOpen(false);
          pushStore.set({ nudge: null });
        }
      }}
      onClose={close}
    />
  );
}

function Card({ title, text, primary, onPrimary, onClose, busy }: { title: string; text: string; primary: string; onPrimary: () => void; onClose: () => void; busy?: boolean }) {
  return (
    <div
      role="dialog"
      aria-labelledby="push-prompt-title"
      className="sheet-in fixed inset-x-3 bottom-[calc(env(safe-area-inset-bottom)+96px)] z-[55] mx-auto max-w-[420px] rounded-[24px] border border-line bg-card p-5 text-fg shadow-pop lg:inset-x-auto lg:right-6 lg:bottom-6"
    >
      <div className="flex items-start gap-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-full bg-accent-bg text-accent-fg">
          <Icon name="bell" size={20} />
        </span>
        <div className="min-w-0 flex-1">
          <h2 id="push-prompt-title" className="m-0 text-[17px] font-semibold">
            {title}
          </h2>
          <p className="m-0 mt-1 text-[14px] leading-snug text-muted">{text}</p>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="grid size-8 shrink-0 place-items-center rounded-full bg-pill text-muted">
          <Icon name="close" size={14} />
        </button>
      </div>
      <div className="mt-4 flex gap-2">
        <button type="button" onClick={onClose} className="h-11 flex-1 rounded-full bg-pill text-[15px] font-semibold text-fg">
          Not now
        </button>
        <button type="button" disabled={busy} onClick={onPrimary} className="h-11 flex-1 rounded-full bg-fg text-[15px] font-semibold text-bg disabled:opacity-60">
          {primary}
        </button>
      </div>
    </div>
  );
}
